import React from "react";
import {Link, Link as RouterLink} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Delete, Edit} from "@material-ui/icons";
import {DatePicker} from "@material-ui/pickers";
import styled from "styled-components";
import {
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useTable,
} from "react-table";
import apiHelper from "../../../../util/ApiHelper";
import {
    CustomCheckbox,
    formatDatetime,
    modal
} from "../../../../util";
import {useLoading} from "../../../../util/LoadingProvider";
import {
    PaginationHandler
} from "../../../../util/useManualPagination";
import {toast} from "react-toastify";
import PaginationBarForHandler from "../../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
    textInput: {
        backgroundColor: "white",
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    inactive: {
        color: "#999999",
    },
    buttonGroup: {
        marginTop: "20px",
        textAlign: "right",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
        background: "#ffffff",
        paddingTop: "10px",
        paddingBottom: "10px",
        minHeight: "800px",
    },
    innerBox: {
        background: "#ffffff",
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
    },
    button: {
        backgroundColor: "#306F9D",
        textTransform: "capitalize",
        color: "white",
        "&:hover": {
            backgroundColor: "#306F9D",
        },
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const statuses = Object.freeze({
    ACTIVE: "active",
    INACTIVE: "inactive",
});

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

function EDMListingPage(props) {
    const {match, disabled} = props;
    const classes = useStyles();
    const [status, setStatus] = React.useState(null);
    const [actionType, setActionType] = React.useState("");
    const [keyword, setKeyword] = React.useState("");
    const [data, setData] = React.useState([]);
    const [totalPages, setTotalPages] = React.useState(1);
    const [selectedDate, handleDateChange] = React.useState(null);
    // const [onChange, setOnChange] = React.useState(true);
    const {setLoading} = useLoading();
    // for pagination
    const [paginationHandler] = React.useState(new PaginationHandler());
    const [elementCounts, setElementCounts] = React.useState({
        all: 0,
        active: 0,
        inactive: 0,
    });

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const handleSelectChange = (e) => {
        setActionType(e.target.value);
    };

    const deleteEDM = async (id) => {
        const decision = await modal.fire({
            title: "Delete eDM",
            text: "Are you sure to delete this eDM?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const result = await apiHelper.hkiff.deleteEDMById(id);
                if (result.data.code === 0) {
                    toast.dark("eDM deleted.");
                    await getEDM();
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Title",
                accessor: "localizations.en.title",
                Cell: ({row}) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.localizations &&
                                row.original.localizations.en &&
                                row.original.localizations.en.title}
                        </Link>
                    );
                },
            },
            {
                Header: "Published Time",
                accessor: "displayDatetime",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return formatDatetime(row.original.displayDatetime);
                },
            },
            {
                Header: "Status",
                accessor: "status.status",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return row.original.status.status ? (
                        <span
                            className={classes.text}>Active</span>
                    ) : (
                        <span
                            className={classes.inactive}>Inactive</span>
                    );
                },
            },
            {
                Header: "Last Update",
                accessor: "updateTime",
                disableGlobalFilter: true,
                Cell: ({row}) => {
                    return formatDatetime(row.original.updateTime);
                },
            },
        ],
        [totalPages]
    );

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        selectedFlatRows,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
            initialState: {pageIndex: 0, pageSize: 10},
            manualPagination: true,
            pageCount: totalPages,
        },
        useGlobalFilter,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex"}}>
                            <IconButton
                                className={classes.iconBtn}
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}`}
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            {!disabled && (
                                <IconButton
                                    className={classes.iconBtn}
                                    size="small"
                                    onClick={() => {
                                        deleteEDM(row.original.id);
                                    }}
                                >
                                    <Delete
                                        fontSize="inherit"/>
                                </IconButton>
                            )}
                        </div>
                    ),
                },
            ]);
        }
    );

    const getEDM = React.useCallback(async () => {
        try {
            setLoading(true);
            // let fetchingPage = 0;
            // let dataContent = [];
            // while (true) {
            const result = await apiHelper.hkiff.getEDM(null, 0, 0, 50, "", false);
            // dataContent = [...dataContent, ...result.data.content];
            // fetchingPage++;
            // if (result.data.totalPages <= fetchingPage) break;
            // await new Promise(resolve => setTimeout(resolve, 500)) // wait for 0.5 second between each batch fetch
            // }
            paginationHandler.setPageIndex(0).setRows(result.data.content);
            const content = paginationHandler.filterRowsByAll().getRows();
            const totalPages = paginationHandler.getTotalPages();
            const all = paginationHandler.getStatuslessRows().length;
            const active = paginationHandler
                .getStatuslessRows()
                .filter((x) => x && x.status && x.status.status).length;
            const inactive = all - active;
            setElementCounts({all, active, inactive});
            setData(content.filter((x) => x));
            setTotalPages(totalPages);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, [paginationHandler, setLoading]);

    React.useEffect(() => {
        if (paginationHandler) {
            getEDM()
        };
    }, [paginationHandler, getEDM]);

    const statusFilterHandler = (targetStatus) => () => {
        setLoading(true);
        setStatus(targetStatus);
        let target;
        if (targetStatus === statuses.ACTIVE) {
            target = true;
        } else if (targetStatus === statuses.INACTIVE) {
            target = false;
        } else {
            target = null;
        }
        paginationHandler
            .resetRows()
            .setQueryStatus("status.status", target)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        gotoPage(0);
        setLoading(false);
    };

    const triggerDateFilter = React.useCallback(() => {
        setLoading(true);
        paginationHandler
            .resetRows()
            .setQueryDate("displayDatetime", selectedDate)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        const all = paginationHandler.getStatuslessRows().length;
        const active = paginationHandler
            .getStatuslessRows()
            .filter((x) => x && x.status.status).length;
        const inactive = all - active;
        setElementCounts({all, active, inactive});
        gotoPage(0);
        setLoading(false);
    }, [gotoPage, paginationHandler, selectedDate, setLoading]);

    const triggerSearch = React.useCallback(() => {
        setLoading(true);
        paginationHandler
            .resetRows()
            .setQueryString("localizations.en.title", keyword)
            .filterRowsByAll();
        setData(paginationHandler.getRows());
        const all = paginationHandler.getStatuslessRows().length;
        const active = paginationHandler
            .getStatuslessRows()
            .filter((x) => x && x.status.status).length;
        const inactive = all - active;
        setElementCounts({all, active, inactive});
        gotoPage(0);
        setLoading(false);
    }, [gotoPage, keyword, paginationHandler, setLoading]);

    const handleBulkAction = React.useCallback(async () => {
        switch (actionType) {
            case "DELETE":
                const ids = selectedFlatRows.map((o) => o.original.id);
                if (ids.length > 0) {
                    const decision = await modal.fire({
                        title: "Delete Guest Accreditation",
                        text: `Are you sure to delete ${ids.length} accreditations(s)?`,
                        showCancelButton: true,
                        confirmButtonText: `Delete`,
                    });
                    if (decision.isConfirmed) {
                        try {
                            const promises = ids.map((id) =>
                                apiHelper.hkiff.deleteEDMById(id)
                            );
                            await Promise.all(promises);
                            toast.dark(`${ids.length} programme(s) deleted.`);
                            await getEDM();
                        } catch (err) {
                            console.log(err);
                        }
                    }
                }
                break;
            default:
                break;
        }
    }, [actionType, selectedFlatRows, getEDM]);

    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                eDM
            </Typography>
            <form>
                <StatusFilter>
                    <li>
                        <button
                            className={status === null ? "active" : ""}
                            type="button"
                            onClick={statusFilterHandler(null)}
                        >
                            <Typography variant="p">All
                                ({elementCounts.all})</Typography>
                        </button>
                    </li>
                    <li>
                        <button
                            className={status === statuses.ACTIVE ? "active" : ""}
                            type="button"
                            onClick={statusFilterHandler(statuses.ACTIVE)}
                        >
                            <Typography variant="p">
                                Active
                                ({elementCounts.active})
                            </Typography>
                        </button>
                    </li>
                    <li>
                        <button
                            className={status === statuses.INACTIVE ? "active" : ""}
                            type="button"
                            onClick={statusFilterHandler(statuses.INACTIVE)}
                        >
                            <Typography variant="p">
                                Drafts
                                ({elementCounts.inactive})
                            </Typography>
                        </button>
                    </li>
                </StatusFilter>
                <Grid
                    container
                    justify="space-between"
                    style={{paddingBottom: "10px"}}
                >
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            alignItems="center"
                        >
                            <Box flex>
                                <FormControl fullWidth
                                             margin="dense"
                                             variant="outlined">
                                    <InputLabel
                                        id="demo-simple-select-outlined-label">
                                        Bulk Actions
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-outlined-label"
                                        id="demo-simple-select-outlined"
                                        value={actionType}
                                        onChange={handleSelectChange}
                                        label="Bulk Actions"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        <MenuItem
                                            value={"DELETE"}>Delete</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box flex>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleBulkAction}
                                    disabled={disabled}
                                >
                                    Apply
                                </Button>
                            </Box>
                            <Box flex>
                                <DatePicker
                                    fullWidth
                                    variant="inline"
                                    inputVariant="outlined"
                                    openTo="year"
                                    margin="dense"
                                    views={["year", "month"]}
                                    label="Year and Month"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                />
                            </Box>
                            <Box flex>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                        triggerDateFilter(selectedDate);
                                    }}
                                >
                                    Filter
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid container item xs={6} md={6}>
                        <Box
                            width="100%"
                            component="span"
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Box display="flex">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="button"
                                    component={RouterLink}
                                    disabled={disabled}
                                    to={`${match.url}/new`}
                                >
                                    Add New Post
                                </Button>
                            </Box>
                            <Box display="flex">
                                <TextField
                                    id="outlined-error"
                                    label="Search"
                                    variant="outlined"
                                    margin="dense"
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </Box>
                            <Box display="flex">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    type="button"
                                    margin="dense"
                                    onClick={triggerSearch}
                                >
                                    Search
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Grid container xs={12}>
                    <TableContainer
                        component={Paper}
                        style={{
                            boxShadow: "none",
                            border: "1px solid #cccccc",
                        }}
                    >
                        <fieldset disabled={disabled}
                                  className="fieldset">
                            <Table
                                aria-label="simple table"
                                component="ul"
                                style={{margin: "0"}}
                                {...getTableProps()}
                            >
                                <colgroup>
                                    <col
                                        style={{width: "5%"}}/>
                                    <col
                                        style={{width: "50%"}}/>
                                    <col
                                        style={{width: "15%"}}/>
                                    <col
                                        style={{width: "10%"}}/>
                                    <col
                                        style={{width: "15%"}}/>
                                    <col
                                        style={{width: "5%"}}/>
                                </colgroup>
                                <TableHead component="div">
                                    {headerGroups.map((headerGroup) => (
                                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <TableCell
                                                    {...column.getHeaderProps()}
                                                    className={`${column.className ?? ""}`}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        lineHeight: "1.1em",
                                                        height: "48px",
                                                    }}
                                                >
                                                    {column.render("Header")}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, index) => {
                                        prepareRow(row);
                                        return (
                                            <TableRow {...row.getRowProps()}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                        <TableCell
                                                            {...cell.getCellProps()}
                                                            className={`${cell.column.className ?? ""}`}
                                                            style={{
                                                                paddingTop: 6,
                                                                paddingBottom: 6,
                                                                lineHeight: "1.1em",
                                                                height: "48px",
                                                            }}
                                                        >
                                                            {cell.render("Cell")}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </fieldset>
                    </TableContainer>
                </Grid>
                <Grid
                    container
                    justify="flex-end"
                    style={{
                        paddingRight: "25px",
                        marginTop: "15px"
                    }}
                >
                    <div className={classes.flexRow}>
                        <div></div>
                        <PaginationBarForHandler
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            setData={setData}
                            setTotalPages={setTotalPages}
                            paginationHandler={paginationHandler}
                            gotoPage={gotoPage}
                            pageIndex={pageIndex}
                            canPreviousPage={canPreviousPage}
                            previousPage={previousPage}
                            nextPage={nextPage}
                            canNextPage={canNextPage}
                            elementCounts={elementCounts}
                            status={status}
                        />
                    </div>
                </Grid>
            </form>
        </div>
    );
}

export default EDMListingPage;
